import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, filter, map, merge, switchMap, take } from 'rxjs';

export const CHAT_ROOM_ID_QUERY_PARAM = 'chatRoomId';
export const CHAT_ROOM_MESSAGE_ID_QUERY_PARAM = 'chatMessageId';

@UntilDestroy()
@Injectable()
export class ChatRedirectionHelperService {
  constructor(private readonly router: Router, private readonly activatedRoute: ActivatedRoute) {}

  private readonly currentChatRoomConfigToDisplay$ = new BehaviorSubject<{
    [CHAT_ROOM_ID_QUERY_PARAM]: string | undefined;
    [CHAT_ROOM_MESSAGE_ID_QUERY_PARAM]: string | undefined;
  }>({
    [CHAT_ROOM_ID_QUERY_PARAM]: undefined,
    [CHAT_ROOM_MESSAGE_ID_QUERY_PARAM]: undefined,
  });

  private readonly queryParams$ = merge(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      switchMap(() => this.activatedRoute.queryParams),
    ),
    this.activatedRoute.queryParams.pipe(take(1)),
  );

  public readonly activeChatQueryParams$ = this.queryParams$.pipe(
    map((queryParams) => {
      const chatRoomId = queryParams[CHAT_ROOM_ID_QUERY_PARAM] as string | undefined;
      const chatRoomMessageId = queryParams[CHAT_ROOM_MESSAGE_ID_QUERY_PARAM] as string | undefined;

      return {
        [CHAT_ROOM_ID_QUERY_PARAM]: chatRoomId,
        [CHAT_ROOM_MESSAGE_ID_QUERY_PARAM]: chatRoomMessageId,
      };
    }),
    untilDestroyed(this),
  );

  public readonly chatRoomConfigToDisplay$ = this.currentChatRoomConfigToDisplay$.pipe(untilDestroyed(this));

  public setChatRoomConfigToDisplay(config: {
    chatRoomId: string | undefined;
    chatMessageId: string | undefined;
  }): void {
    this.currentChatRoomConfigToDisplay$.next(config);
  }
}
